import React from 'react';
import { ClickAwayListener } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AssistantComposer } from 'src/modules/assistant/components/AssistantComposer';
import { AssistantLauncher } from 'src/modules/assistant/components/AssistantLauncher';
import { AssistantMessageList } from 'src/modules/assistant/components/AssistantMessageList';
import { AssistantTabs } from 'src/modules/assistant/components/AssistantTabs';
import { NextActionSuggestion } from 'src/modules/assistant/components/NextActionSuggestion';
import { WorkingLeadsList } from 'src/modules/assistant/components/WorkingLeadsList';
import {
    ASSISTANT_CHAT_REGION_ID,
    ASSISTANT_NAME,
    ASSISTANT_SECTION,
} from 'src/modules/assistant/constants/widget';
import { useAssistantWidget } from 'src/modules/assistant/hooks/useAssistantWidget';
import { useWidgetStyles } from './styles';

const AssistantWidget = () => {
    const classes = useWidgetStyles();
    const { t } = useTranslation();
    const widget = useAssistantWidget();
    const { assistant, open } = widget;
    const { busy, loading, error, context } = assistant;

    if (!assistant.assistantVisible) return null;

    return (
        <ClickAwayListener onClickAway={widget.closeWidget}>
            <div
                className={`${classes.shell} ${open ? classes.shellOpen : ''} ${
                    busy && !open ? classes.shellBusy : ''
                }`}
                onTransitionEnd={widget.handleShellTransitionEnd}
            >
                <AssistantLauncher
                    busy={busy}
                    greeting={widget.greeting}
                    listening={widget.listening}
                    onClose={widget.closeWidget}
                    onOpen={widget.openWidget}
                    open={open}
                    ref={widget.launcherRef}
                    responseReady={widget.responseReady}
                />
                {open && (
                    <div
                        aria-label={t('Assistant.ChatRegion', {
                            name: ASSISTANT_NAME,
                        })}
                        className={classes.chat}
                        id={ASSISTANT_CHAT_REGION_ID}
                        role="region"
                    >
                        <AssistantTabs
                            onSelectChat={widget.selectChat}
                            onSelectWorkingLeads={widget.selectWorkingLeads}
                            scope={context.scope}
                            section={widget.section}
                            showLeadTab={Boolean(assistant.selectedLead)}
                        />
                        <div
                            aria-live="polite"
                            className={classes.transcript}
                            ref={widget.transcriptRef}
                        >
                            {widget.section ===
                                ASSISTANT_SECTION.WORKING_LEADS && (
                                <WorkingLeadsList
                                    hasMore={assistant.workingLeadsHasMore}
                                    loading={assistant.workingLeadsLoading}
                                    onLoadMore={widget.loadMoreWorkingLeads}
                                    onSelect={widget.closeWidget}
                                    workingLeads={assistant.workingLeads}
                                />
                            )}
                            {widget.inChat && (
                                <AssistantMessageList
                                    actions={widget.messageActions}
                                    busy={busy}
                                    canUseAssistant={assistant.canUseAssistant}
                                    contextLoading={
                                        assistant.assistantContextLoading
                                    }
                                    error={error}
                                    formatDateText={widget.formatDateText}
                                    leadRequiresAgent={widget.leadRequiresAgent}
                                    loading={loading}
                                    messages={assistant.messages}
                                    scope={context.scope}
                                />
                            )}
                        </div>
                        {widget.showNextActionSuggestion && (
                            <NextActionSuggestion
                                disabled={widget.requestDisabled}
                                onRequest={widget.requestNextAction}
                            />
                        )}
                        {widget.inChat && assistant.canUseAssistant && (
                            <AssistantComposer
                                disabled={widget.requestDisabled}
                                key={widget.composerKey}
                                onListeningChange={widget.setListening}
                                onSubmit={widget.submitMessage}
                            />
                        )}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};

export default AssistantWidget;
